// EventList.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { 
    Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme, 
    Card, CardContent, Divider, IconButton, 
    SvgIcon,
    FormControlLabel,
    Checkbox,
    ToggleButton
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar, type GridRenderCellParams } from '@mui/x-data-grid';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CheckBox, Visibility } from '@mui/icons-material'; // Для мобильной кнопки просмотра
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import dayjs, { Dayjs } from "dayjs";
import { api } from "../../../api/eventApi";
import { api as groupApi } from "../../../api/groupApi";
import { EventRecord } from "../../../types/event.ts";
import { useFetch } from "../../../hooks/useFetch2";
import { useTranslation } from 'react-i18next';
import ActionButtons from '../../ActionButtons.tsx';
import { FormModal } from '../../modal/FormModal.tsx';
import EventForm from './EventForm.tsx';
import { dataToExcel } from '../../../tools/dataToExcel.ts';
import LocalizedGrid from '../../localizedGrid/LocalizedGridCook3.tsx';
import i18n from '../../../i18n';
import { Group } from '../../../types/group.ts';
import { ToolbarToggleButton } from '../../ToolbarToggleButton.tsx';
import { LocalizedDatePicker } from '../../localizedDatePicker/LocalizedDatePicker.tsx';
import { formatDateTime } from '../../../tools/util.ts';
import { useParams } from 'react-router-dom';

export const EventList: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [from, setFrom] = useState<Dayjs | null>(dayjs().startOf("day"));
    const [to, setTo] = useState<Dayjs | null>(dayjs().endOf("day"));
    const { data, setData, error, update, loading } = useFetch<EventRecord[]>(api.get, [], {
        from: from?.toISOString(),
        to: to?.toISOString(),
    });
    const { data: groups } = useFetch<Group[]>(groupApi.get, []);
    

    const distinctOwners = [...new Set(data.map(event => event.car_owner))];
    
    const [isExporting, setIsExporting] = useState(false);

    const [selectedEvent, setSelectedEvent] = useState<EventRecord | undefined>();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { paramId } = useParams();
    useEffect(() => {
        if (paramId) {
            const event = data.find(e => e.id === +paramId);
            if (!event) return;
            setSelectedEvent(event);
            setIsModalOpen(true);
        }
    }, [paramId, data]);

    // const columns: GridColDef[] = [
    const columns: GridColDef[] = useMemo(() => [        
        { field: "id", headerName: "ID", type: "number", flex: 1, width: 50 },
        { field: "point_name", headerName: t('EventList.point_name'), type: "string", flex: 2, width: 150 },
        { field: "checkpoint_name", headerName: t('EventList.checkpoint_name'), type: "string", flex: 2, width: 150 },
        { field: "origin_name", headerName: t('EventList.origin_name'), type: "string", flex: 2, width: 150 },
        { field: "ts_start", headerName: t('EventList.ts_start'), type: "string", flex: 2, width: 200, valueFormatter: (v) => formatDateTime(v), },
        { field: "registration_number", headerName: t('EventList.registration_number'), type: "string", flex: 2, width: 100, hideable: false, },
        { field: "group_id", headerName: t('PlatesList.group'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: groups.map( group => ({value: group.group_id, label: group.name})),
            editable: false,
            renderCell: (params: GridRenderCellParams<Group>) => {
                return (
                    <Typography>
                        {groups.find(g => g.group_id === params.row.group_id)?.name}
                    </Typography>
                );
            }
        },
        { field: "car_brand", headerName: t('EventList.car_brand'), type: "string", flex: 2, width: 100 },
        { field: "car_owner", headerName: t('EventList.car_owner'), type: "singleSelect", flex: 2, width: 100, 
            valueOptions: distinctOwners.map( value => ({value, label: value})),
        },
        { field: "is_enter", headerName: t('EventList.is_enter'), type: "string", flex: 2, width: 100, 
            display: 'flex', // Включаем flex для ячейки
            alignItems: 'center', // Центрируем по вертикали
            renderCell: (params: GridRenderCellParams<EventRecord>) => (
                // <Typography color={params.value ? 'success.main' : 'error.main'} fontWeight="bold">
                <Typography color={params.value ? 'success.main' : 'error.main'}>
                    {params.value == undefined ? '' : (params.value ? t('EventList.enter') : t('EventList.exit'))}
                </Typography>
            ),
        },
        { field: "tag", headerName: t('EventList.tag'), type: "string", flex: 2, width: 100 },
        {
            field: 'actions',
            headerName: t('EventList.actions'),
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<EventRecord>) => (
                <ActionButtons actions={[{ type: 'extended', action: () => handleOpenModal(params.row), icon: PhotoCamera }]} />
            ),
        },
    // ];
    ], [t, groups, distinctOwners]);

    const [showToolbar, setShowToolbar] = useState<boolean>(false);

    const handleRefresh = () => {
        setData([]);
        update({ from: from?.toISOString(), to: to?.toISOString() });
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const temp = data.map(({ ts_start, ts_end, group_id, ...rest }) => ({
                ...rest,
                ts: formatDateTime(ts_start),
                group_id,
                group_name: groups.find(g => g.group_id === group_id)?.name
            }));
            dataToExcel(temp, 'lpr_event');
        } catch (e) {
            console.error("Export failed:", e);
        } finally {
            setIsExporting(false);
        }
    };

    const handleOpenModal = (row: EventRecord) => {
        setSelectedEvent(row);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);

    // --- Мобильная карточка ---
    const renderMobileCard = (event: EventRecord) => (
        <Card key={event.id} sx={{ mb: 2, boxShadow: 1, border: `1px solid ${theme.palette.divider}` }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                            {event.registration_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {event.ts_start}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => handleOpenModal(event)} size="small">
                        <Visibility color="action" />
                    </IconButton>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Stack spacing={0.5}>
                    <Typography variant="body2"><strong>{t('EventList.origin_name')}:</strong> {event.origin_name}</Typography>
                    <Typography variant="body2"><strong>{t('EventList.checkpoint_name')}:</strong> {event.checkpoint_name}</Typography>
                    <Typography variant="body2"><strong>{t('EventList.car_owner')}:</strong> {event.car_owner || '—'}</Typography>
                    <Typography variant="body2"><strong>{t('EventList.car_brand')}:</strong> {event.car_brand || '—'}</Typography>
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Paper 
                elevation={isMobile ? 0 : 1} 
                sx={{ p: isMobile ? 1 : 3, maxWidth: 1600, mx: "auto", mt: isMobile ? 1 : 4, bgcolor: isMobile ? 'transparent' : 'background.paper' }}
            >
                {/* Фильтры */}
                <Stack 
                    direction={isMobile ? "column" : "row"} 
                    spacing={2} 
                    alignItems={isMobile ? "stretch" : "center"} 
                    mb={3}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ minWidth: 20 }}>{t('EventList.from')}</Typography>
                        <LocalizedDatePicker
                            value={from}
                            onChange={(v) => setFrom(v)}
                            // label="Выберите дату from"
                            // slotProps={{ textField: { size: "small", fullWidth: true } }}
                            // format={'DD.MM.YYYY'}
                        />
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ minWidth: 20 }}>{t('EventList.to')}</Typography>
                        <LocalizedDatePicker
                            value={to}
                            onChange={(v) => setTo(v)}
                            // label="Выберите дату to"
                            // slotProps={{ textField: { size: "small", fullWidth: true } }}
                            // format={'DD.MM.YYYY'}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <Button
                            fullWidth={isMobile}
                            variant="contained"
                            onClick={handleRefresh}
                            sx={{ flexGrow: 1 }}
                            // sx={{ bgcolor: "yellow", color: "black", "&:hover": { bgcolor: "#ffee00" }, flexGrow: 1 }}
                        >
                            {t('EventList.refresh')}
                        </Button>
                        <Button
                            fullWidth={isMobile}
                            variant="outlined"
                            onClick={handleExport}
                            disabled={isExporting}
                            sx={{ flexGrow: 1 }}
                        >
                            {t('EventList.export')}
                        </Button>

                        {/* {!isMobile && (
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={showToolbar} 
                                    onChange={(e) => setShowToolbar(e.target.checked)} 
                                    size="small"
                                />
                            }
                            label={t('EventList.show_toolbar')}
                            sx={{ ml: 1, whiteSpace: 'nowrap' }}
                        />
                        )} */}

                        {/* {!isMobile && (
                        <ToggleButton
                            value="check" // Обязательный пропс для ToggleButton
                            selected={showToolbar}
                            onChange={() => {
                            setShowToolbar(!showToolbar);
                            }}
                            size="small"
                            color="primary"
                            sx={{ 
                            ml: 1, 
                            whiteSpace: 'nowrap',
                            height: '38px', // Подгоняем под высоту других кнопок/инпутов
                            px: 2 
                            }}
                        >
                            {t('EventList.show_toolbar')}
                        </ToggleButton>
                        )} */}

                        {!isMobile && (
                        <ToolbarToggleButton 
                            isOpen={showToolbar} 
                            onToggleState={setShowToolbar} 
                        />
                        )}                        
                    </Stack>
                </Stack>

                {/* Контент: Таблица или Карточки */}
                {isMobile ? (
                    <Box>
                        {data.map(event => renderMobileCard(event))}
                        {data.length === 0 && <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>{t('common.no_data')}</Typography>}
                    </Box>
                ) : (
                    <LocalizedGrid
                        lang={i18n.language as any}
                        gridId='EventListMobile'
                        rows={data}
                        columns={columns}
                        getRowId={(row) => row.id}
                        // autoHeight
                        sx={{ 
                            border: 'none', 
                            height: 'calc(100vh - 340px)' // Вычитаем высоту хедера и отступов
                        }}
                        initialState={{
                            columns: {columnVisibilityModel: {id: false}}
                        }}
                        slots={{ toolbar: GridToolbar }}
                        hideToolbar={!showToolbar}
                        loading={loading}
                    />
                )}

                <Typography color='error' sx={{ mt: 2 }}>{error?.message}</Typography>
            </Paper>

            <FormModal open={isModalOpen} onClose={handleCloseModal} ratio={2/3}>
                <EventForm
                    eventRecord={selectedEvent}
                    onCancel={handleCloseModal}
                />
            </FormModal>
        </LocalizationProvider>
    );
}